import { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Modal from 'react-modal';
import { clone, prop } from 'ramda';
import * as FieldConfigs from './FieldConfigs';
import SaveCancelButton from '../save-cancel-button/SaveCancelButton.jsx';
import ChangeTypeSelector from './ChangeTypeSelector.jsx';
import ConfirmationModal from '../confirmation-modal/ConfirmationModal.jsx';

/**
 * The BulkChangeConfigurator allows the user to select multiple POIs and make changes all at once to various fields. Each tab has fields corresponding to
 * different areas that can be changed. For each field, a dropdown lets the user choose which action that they want to bulk perform on the selected POIs,
 * and if editing, then a secondary input field allows the user to choose what the field for the selected data should be changed to. Once the user is satisfied
 * with all the changes, then clicking on the save button will trigger all the changed fields to be saved
 *
 * @param {object} changes: Stores the changed properties
 * @param {number} selectedTabIndex: Index of the tab that is being displayed
 * @param {string} selectedField: Field that is being edited/deleted
 *
 * Static Labels and Buttons
 * @param {string} okayLabel Label for the Save button to save the changes
 * @param {string} cancelLabel Label for the Cancel button to remove all the changes you've made
 * @param {function} onClose Checks if anything has been changed. If it has, then it sets saving to true and then closes the modal
 * @param {boolean} open Whether the Bulk Editor modal is open
 * @param {number} selectedPoiCount Number of POIs that are being bulk edited
 * @param {boolean} saving When the save button is pressed in the modal, saving is set to true. Once saved, set to false again
 *
 * Example of object the object with all the changes from Bulk Editor:
 *  changes = {
      name: {
        value: 'foo',
        changeType: 'REPLACE',
        isValidChange: false
      },
      displayTags: {
        value: ['bread', 'ink'],
        changeType: 'ADD'
      }
    }
 *
 */

import s from './BulkChangeConfigurator.module.scss';
import Spinner from '../spinner/Spinner';

Modal.setAppElement('#root');

const sOrNot = count => (count === 1 ? '' : 's'); // Variable for adding an 's' to the end of words that are more than one

const makeChangeLabel = count => `${count} field${sOrNot(count)} bulk edited`;
const makeSelectionLabel = count => `${count} POI${sOrNot(count)} SELECTED`;

export default class BulkChangeConfigurator extends Component {
    static propTypes = {
        accountId: PropTypes.string,
        cancelLabel: PropTypes.string,
        categories: PropTypes.arrayOf(PropTypes.object),
        getBadgeOptions: PropTypes.func,
        isV5: PropTypes.bool,
        okLabel: PropTypes.string,
        onClose: PropTypes.func,
        open: PropTypes.bool,
        saving: PropTypes.bool,
        selectedPoiCount: PropTypes.number,
        venueId: PropTypes.string,
    };

    static defaultProps = {
        accountId: '',
        cancelLabel: 'CANCEL',
        categories: [],
        getBadgeOptions: () => [],
        isV5: true,
        okLabel: 'SAVE',
        onClose: () => {},
        open: false,
        saving: false,
        selectedPoiCount: 2,
        venueId: '',
    };

    constructor(props) {
        super(props);
        this.state = {
            changes: {},
            selectedTabIndex: 0,
            selectedField: '',
            showConfirmationModal: false,
            displayTagsEditType: '',
            hiddenTagsEditType: '',
        };
    }

    renderCancelModal = () => {
        const { showConfirmationModal } = this.state;
        const modalFields = { prompt: 'Your changes will be lost. Are you sure?', confirmLabel: 'YES', cancelLabel: 'NO' };
        if (showConfirmationModal) {
            return (
                <ConfirmationModal
                    title={modalFields.prompt}
                    subtitle=""
                    okLabel={modalFields.confirmLabel}
                    cancelLabel={modalFields.cancelLabel}
                    open={showConfirmationModal}
                    onClose={this.onCloseConfirmation}
                />
            );
        }
    };

    /**
     * Brings up and tracks any changes in the Bulk Editor.
     * @param {object} fieldConfig: Object for each field that contains its properties
     *
     */
    renderBulkFieldSelector = fieldConfig => {
        const hasChanges = !!this.state.changes[fieldConfig.field]; // Check if changes object contains any data
        const selected = fieldConfig.field === this.state.selectedField; // Check for current tab
        return (
            <div className={classNames(s.fieldButton, { [s.dirty]: hasChanges, [s.selected]: selected })} key={fieldConfig.field} onClick={() => this.setState({ selectedField: fieldConfig.field })}>
                {fieldConfig.title}
            </div>
        );
    };

    renderEditor = () => {
        const { selectedField, changes } = this.state;
        const { categories, isV5, getBadgeOptions, venueId, accountId } = this.props;
        const fieldConfig = Object.values(FieldConfigs.BULK_FIELDS).find(field => field.field === selectedField); // Object containing info for the form for each field's bulk action

        if (!fieldConfig) {
            return (
                <div className={s.editorWrapper}>
                    <div className={s.landingText}>
                        Get started by selecting the field(s)
                        <br />
                        that you would like to bulk edit
                    </div>
                </div>
            );
        }

        const Editor = fieldConfig.editor || (() => <div>No editor found</div>); // Text field editor
        const { changeType, value, subValue, isValid } = changes[selectedField] || {};
        const defaultValue = clone(prop('defaultValue', fieldConfig)); // need to kill the reference to the original array so the 'defaultValue' is not the value being updated

        return (
            <div className={s.editorWrapper}>
                <div className={s.editorTitle}>{fieldConfig.title}</div>
                <div className={s.editorSubtitle}>Select a bulk action to perform</div>
                <ChangeTypeSelector options={FieldConfigs.makeChangeTypeOptions(selectedField)} onChange={this.onSelectChangeType} value={changeType} />
                <Editor
                    accountId={accountId}
                    categories={categories}
                    changeType={changeType}
                    fieldConfig={fieldConfig}
                    getBadgeOptions={getBadgeOptions}
                    isV5={isV5}
                    isValid={isValid}
                    onChange={this.onChangeEditorValue}
                    onReset={this.onResetEditor}
                    onSubChange={this.onChangeSubEditorValue}
                    placeHolder={fieldConfig.placeholder}
                    subValue={subValue || defaultValue || ''}
                    value={value || defaultValue || ''}
                    venueId={venueId}
                    {...fieldConfig.editorConfig}
                />
            </div>
        );
    };

    /**
     * When a bulk action has been selected for a field, it updates the change object to have an additional property
     * @param {string} changeType: The action that was selected to be performed on the bulk data
     *
     */
    onSelectChangeType = changeType => {
        const { selectedField, changes } = this.state;
        if (changeType === null) {
            const { [selectedField]: _, ...remainingChanges } = changes;
            this.setState({ changes: remainingChanges });
        } else {
            const value = changes[selectedField]?.value;
            const subValue = changes[selectedField]?.subValue;
            const isValid = FieldConfigs.isValidChange(changeType, value, subValue);
            this.setState({ changes: { ...changes, [selectedField]: { value, subValue, changeType, isValid } } });

            // Need to know specifically what edit happened here since the fields updated are arrays
            if (selectedField === 'displayTags') this.setState({ displayTagsEditType: changeType });
            if (selectedField === 'tags') this.setState({ hiddenTagsEditType: changeType });
        }
    };

    /**
     * Changes the value in the current change object
     * @param {string} value: The current string that is typed in when replacing a property's string
     *
     */
    onChangeEditorValue = value => {
        const { selectedField, changes } = this.state;
        const { changeType } = changes[selectedField];
        const subValue = '';
        const isValid = FieldConfigs.isValidChange(changeType, value, subValue);
        const newChanges = { ...changes, [selectedField]: { value, subValue, changeType, isValid } };
        this.setState({ changes: newChanges });
    };

    /**
     * When in the case of two values being used (parent and its child), this function validates that child
     * @param {string} subValue: The current string retrived from mapping the const values from the original parent category
     *
     */
    onChangeSubEditorValue = subValue => {
        const { selectedField, changes } = this.state;
        const { changeType, value } = changes[selectedField];
        const isValid = FieldConfigs.isValidChange(changeType, value, subValue);
        const newChanges = { ...changes, [selectedField]: { value: changes[selectedField].value, subValue, changeType, isValid } };
        this.setState({ changes: newChanges });
    };

    /**
     * User "unconfigures" bulk change in an editor- i.e. don't edit this field
     *
     */
    onResetEditor = () => {
        const { selectedField, changes } = this.state;
        const { [selectedField]: _, ...remainingChanges } = changes;
        this.setState({ changes: remainingChanges });
    };

    /**
     * Set the state with the current tab
     * @param {number} newIndex: The index of the current tab
     *
     */
    onSelectTab = newIndex => {
        if (this.state.selectedTabIndex === newIndex) {
            return;
        }
        const selectedField = FieldConfigs.TABS[newIndex]?.fields[0];
        this.setState({ selectedTabIndex: newIndex, selectedField });
    };

    /**
     * Creates the tabs for the Bulk Editor, where each tab contains fields that can be changed or deleted
     * @param {object} tabConfig: Object containing the name of the tab and an array of its fields
     * @param {number} index: The index corresponding to each tab
     *
     */
    renderTabItem = (tabConfig, index) => {
        const isSelected = index === this.state.selectedTabIndex;
        return (
            <div data-testid={`${tabConfig.title}-tab`} key={tabConfig.title} className={classNames(s.tabItem, { [s.selected]: isSelected })} onClick={() => this.onSelectTab(index)}>
                {tabConfig.title}
            </div>
        );
    };

    /**
     * Pulls out all invalid changes and drop the "isValid" field before submitting configured changes to parent
     *
     */
    onSubmitChangeConfiguration = () => {
        const { onClose } = this.props;
        const { displayTagsEditType, hiddenTagsEditType } = this.state;
        const changes = clone(this.state.changes);

        // Check if changes contains category and icon, if so, separate
        if ('category' in changes && changes.category.isValid === true) {
            changes.icon = { value: changes.category.subValue, changeType: 'ICON_CHANGE', isValid: true };
            delete changes.category.subValue;
        }

        Object.keys(changes).forEach(fieldName => {
            if (!changes[fieldName].isValid) {
                delete changes[fieldName];
            } else {
                delete changes[fieldName].isValid;
            }
        });

        const arrayEditTypes = {
            displayTagsEditType,
            hiddenTagsEditType,
        };

        onClose(changes, arrayEditTypes);
    };

    onCloseConfirmation = userResponse => {
        const { onClose } = this.props;
        if (userResponse) {
            this.onResetEditor();
            onClose(false);
        }
        this.setState({ showConfirmationModal: false });
    };

    render() {
        const { selectedPoiCount, okLabel, cancelLabel, onClose, open, saving, isTranslatedVenue } = this.props;
        const { selectedTabIndex, changes } = this.state;
        let changeCount = 0;

        Object.values(changes).forEach(change => {
            if (change.isValid) {
                changeCount += 1;
            }
        });
        const currentFieldSet = FieldConfigs.TABS[selectedTabIndex].fields.map(fieldName => FieldConfigs.BULK_FIELDS[fieldName]); // Contains field information for all tabs
        return (
            <Modal isOpen={open} className={s.modal} overlayClassName={classNames(s.overlay, saving ? s.savingOverlay : '')}>
                <div className={s.titleWrapper}>
                    <div className={s.title}>Bulk Information Editor</div>
                    <div className={s.selectionLabel}>{makeSelectionLabel(selectedPoiCount)}</div>
                </div>
                <div className={s.tabBar}>{FieldConfigs.TABS.map(this.renderTabItem)}</div>
                <div className={s.contentArea}>
                    <div className={s.fieldList}>{currentFieldSet.map(this.renderBulkFieldSelector)}</div>
                    {this.renderEditor()}
                </div>
                <div className={s.buttonGroup}>
                    <SaveCancelButton
                        wide
                        saveLabel={okLabel}
                        cancelLabel={cancelLabel}
                        saveDisabled={changeCount === 0 || isTranslatedVenue}
                        onCancel={
                            changeCount !== 0
                                ? () => {
                                      this.setState({ showConfirmationModal: true });
                                  }
                                : () => onClose()
                        }
                        onSave={this.onSubmitChangeConfiguration}
                        changeCount={changeCount}
                        changeLabel={makeChangeLabel(changeCount)}
                    />
                    {saving && <div className={s.savingIndicator} />}
                </div>
                {this.renderCancelModal()}
                <Spinner loading={saving} />
            </Modal>
        );
    }
}
